import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: props.color ? `${props.color}20` : '#f8f9fa',
    borderRadius: 12,
    minWidth: 300,
    maxWidth: 300,
    padding: theme.spacing(1),
    marginRight: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    border: `2px solid ${props.color || '#e9ecef'}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  }),
  columnTitle: {
    marginBottom: theme.spacing(0.25),
    fontWeight: 600,
    fontSize: '0.9rem',
    color: "#495057",
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    maxHeight: 'calc(100vh - 200px)',
    paddingTop: theme.spacing(0.25),
  },
  totalValue: {
    fontSize: '0.85rem',
    color: "#6c757d",
    fontWeight: 500,
    backgroundColor: '#e9ecef',
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    marginLeft: 'auto',
  },
  columnHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(0.75),
    borderBottom: '2px solid #dee2e6',
  },
  dragHandle: {
    cursor: 'grab',
    color: '#6c757d',
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    '&:hover': {
      color: '#495057',
    },
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  ticketCount: {
    fontSize: '0.75rem',
    color: '#6c757d',
    fontWeight: 500,
    marginLeft: theme.spacing(0.5),
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin }) => {
  const classes = useStyles({ color });

  const totalValue = tickets.reduce((acc, ticket) => {
    if (!ticket.contact || !ticket.contact.extraInfo) return acc;
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    if (!valueField || !valueField.value) return acc;
    const opportunityValue = parseFloat(valueField.value) || 0;
    return acc + opportunityValue;
  }, 0);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {isAdmin && (
                <div
                  {...provided.dragHandleProps}
                  className={classes.dragHandle}
                >
                  <DragIndicatorIcon fontSize="small" />
                </div>
              )}
              <div>
                <Typography className={classes.columnTitle}>{title}</Typography>
                <Typography className={classes.ticketCount}>
                  {tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}
                </Typography>
              </div>
            </div>
            <Typography className={classes.totalValue}>
              R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                    laneColor={color}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanColumn;
